"use client";
import PageBuilder from "@/components/PageBuilder";
import { formatDate, getTitleByCategory } from "@/lib/globalFunctions";
import { PortableText } from "@portabletext/react";
import { FaLinkedinIn, FaRegCheckCircle } from "react-icons/fa";
// import { useRouter } from "next/router";
import ScrollToTop from "@/components/atoms/ScrollToTop";
import SmallProgressBar from "@/components/atoms/SmallProgressBar";
import EdenRichText from "@/components/molecules/EdenRichText/EdenRichText";
import ImageGallery from "@/components/molecules/ImageGallery/ImageGallery";
import { Button } from "@/components/ui/button";
import { useGlobalStore } from "@/lib/globalStore";
import { urlForImage } from "@/sanity/lib/image";
import dynamic from "next/dynamic";
import Link from "next/link";
import { FaArrowLeft, FaFacebookF } from "react-icons/fa";
import ReactPlayer from "react-player/lazy";
import { FacebookShareButton, LinkedinShareButton } from "react-share";

type Props = {
  data: any;
  params: any;
};
const DynamicReactPlayer = dynamic(() => Promise.resolve(ReactPlayer), {
  ssr: false,
});

const LearnContent = (props: Props) => {
  const { data, params } = props;
  const { getStartedOpen, setGetStartedOpen } = useGlobalStore();

  const formattedDate = formatDate(data.publishedAt);
  const articlePortableTextComponents: any = {
    types: {
      image: ({ value }: any) => {
        return (
          <div className="max-w-[1128px] py-4 sm:py-10 mx-auto ">
            <img
              src={urlForImage(value).width(1200).format("webp").url() || ""}
              alt={value.alt}
              className="w-full rounded-[32px]"
            />
          </div>
        );
      },
      button: ({ value }: any) => {
        return (
          <div className="max-w-[896px] mx-auto">
            {value.type === "getStarted" ? (
              <div>
                <Button
                  variant={"secondary"}
                  onClick={() => setGetStartedOpen(true)}
                >
                  {value.label}
                </Button>
              </div>
            ) : (
              <div>
                <Link href={value.href}>
                  <Button variant={"secondary"}>{value.label}</Button>
                </Link>
              </div>
            )}
          </div>
        );
      },
      edenContent: ({ value }: any) => {
        return <EdenRichText data={value} />;
      },
      imageGallery: ({ value }: any) => {
        return (
          <div className="max-w-[1128px] py-5 sm:py-10 mx-auto bg-red">
            <ImageGallery data={value} />
          </div>
        );
      },
      video: ({ value }: any) => {
        return (
          <div className="max-w-[1128px] py-10 mx-auto ">
            <div className="player-wrapper !rounded-[32px] w-full ">
              <DynamicReactPlayer
                className="react-player"
                url={value.url}
                width="100%"
                height="100%"
                controls={false}
              />
            </div>
            {value.subtext && (
              <div>
                <small className="text-[12px] sm:text-caption text-[#4A4A68]">
                  {value.subtext}
                </small>
              </div>
            )}
          </div>
        );
      },
    },
    block: {
      normal: ({ children }: any) => (
        <p className="max-w-[896px] text-[#4A4A68] pb-6 mx-auto font-medium text-base leading-[150%]">
          {children}
        </p>
      ),
      h1: ({ children }: any) => (
        <h1 className="max-w-[896px] pb-6 mx-auto text-[3rem] phone:text-h1 font-general-sans font-medium leading-[120%]">
          {children}
        </h1>
      ),
      h2: ({ children }: any) => (
        <h2 className="max-w-[896px] pb-6 mx-auto text-[40px] sm:text-h2 phone:text-h2-mobile font-general-sans leading-[120%]">
          {children}
        </h2>
      ),
      h3: ({ children }: any) => (
        <h3 className="max-w-[896px] pb-6 mx-auto text-[1.75rem] phone:text-h3 font-general-sans leading-[120%]">
          {children}
        </h3>
      ),
      h4: ({ children }: any) => (
        <h4 className="max-w-[896px] pb-3 sm:pb-6 mx-auto font-semibold text-[22px] sm:text-h4 leading-[120%]">
          {children}
        </h4>
      ),
      h5: ({ children }: any) => (
        <h5 className="max-w-[896px] py-5 sm:py-10 mx-auto font-semibold text-xl sm:text-h5 leading-[120%]">
          {children}
        </h5>
      ),
      tag: ({ children }: any) => (
        <p className="text-tag font-general-sans font-medium leading-[120%]">
          {children}
        </p>
      ),
      subtitle: ({ children }: any) => (
        <p className="text-subtitle phone:text-subtitle font-general-sans leading-[160%] py-2">
          {children}
        </p>
      ),
      body: ({ children }: any) => (
        <p className="text-[0.938rem] phone:text-body font-general-sans-variable leading-[140%] py-2">
          {children}
        </p>
      ),
      bodyL: ({ children }: any) => (
        <p className="text-[1rem] phone:text-bodyL font-general-sans-variable leading-[140%] py-2">
          {children}
        </p>
      ),
      caption: ({ children }: any) => (
        <p className="text-[0.75rem] phone:text-caption font-general-sans font-medium leading-[140%] py-2">
          {children}
        </p>
      ),
      blockquote: ({ children }: any) => (
        <p className="max-w-[896px] py-10 mx-auto font-semibold text-[22px] sm:text-quote leading-[120%]">
          {children}
        </p>
      ),
    },
    list: {
      bullet: ({ children }: any) => (
        <ul className="flex max-w-[896px] mx-auto flex-col pb-6 gap-4 leading-[150%]">
          {children.map((child: any, index: any) => {
            return (
              <li
                key={index}
                className="flex ml-4 font-semibold gap-4 items-center"
              >
                <div>
                  <FaRegCheckCircle size={20} />
                </div>
                {child.props.children}{" "}
              </li>
            );
          })}
        </ul>
      ),
      number: ({ children }: any) => (
        <ol className="max-w-[896px] py-10 mx-auto font-medium text-base leading-[150%]">
          {children}
        </ol>
      ),
    },
    marks: {
      link: ({ children, value }: any) => {
        return (
          <Link
            className="underline text-blue-800"
            href={value.url ?? value.href}
          >
            {children}
          </Link>
        );
      },
      tel: ({ children, value }: any) => {
        return (
          <a
            href={`tel:${value.phoneNumber}`}
            className="underline text-blue-800"
          >
            {children}
          </a>
        );
      },
      mailto: ({ children, value }: any) => {
        return (
          <a
            href={`mailto:${value.mailAddress}`}
            className="underline text-blue-800"
          >
            {children}
          </a>
        );
      },
      internalLink: ({ children, value }: any) => {
        let baseUrl;
        switch (value.reference._type) {
          case "post":
            baseUrl = `/learn/${value.reference.slug.current}`;
            break;
          case "page":
            baseUrl = `/${value.reference.slug.current}`;
            break;
          case "product":
            baseUrl = `/products/${value.reference.category.slug.current}/${value.reference.slug.current}`;
            break;
          default:
            baseUrl = "/";
        }
        return (
          <Link className="underline text-blue-800" href={baseUrl}>
            {children}
          </Link>
        );
      },
    },
  };
  // return <div className="container text-white">ID: {params.slug}</div>;
  return (
    <div className=" bg-[#F5F5F6] relative text-main-dark-green">
      <ScrollToTop />
      <div className="z-10 bg-white top-[-1px] sticky">
        <div className="h-[60px] flex gap-4 container w-full">
          <Link
            className="group flex mr-8 items-center h-full gap-2 mb-8"
            href={`/learn`}
          >
            <FaArrowLeft />
            <p className="text-sm group-hover:underline font-bold">
              Back{" "}
              <span className="hidden sm:inline-block">to Eden's world</span>
            </p>
          </Link>
          <div className="hidden sm:flex items-center ">
            <p className="text-sm">
              <span className="font-semibold">You are reading: </span>
              {data.title}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <p>Share:</p>
            <LinkedinShareButton
              url={`https://enviroplusproducts.com.au/learn/${params.slug}`}
            >
              <div className="flex items-center justify-center text-white rounded-full bg-main-dark-green">
                <FaLinkedinIn size={32} className="p-2" />
              </div>
            </LinkedinShareButton>
            <FacebookShareButton
              url={`https://enviroplusproducts.com.au/learn/${params.slug}`}
            >
              <div className="flex items-center justify-center text-white rounded-full  bg-main-dark-green">
                <FaFacebookF size={32} className="p-2" />
              </div>
            </FacebookShareButton>
          </div>
        </div>
        <SmallProgressBar />
      </div>
      <div className="container">
        <div className="pt-10 sm:pt-20 pb-4 sm:pb-10 max-w-[896px] mx-auto">
          <div className="flex gap-8 items-center">
            <Button variant={"secondary"} className="!border-none bg-white">
              {getTitleByCategory(data.postCategory)}{" "}
            </Button>
            <div className="text-sm font-semibold text-main-dark-green">
              {formattedDate}
            </div>
            <div className="text-sm font-semibold text-main-dark-green">
              {data.estimatedReadTime} mins read time
            </div>
          </div>
          <div>
            <h1 className="font-bold leading-none sm:leading-[120%] font-roobert text-h2-mobile sm:text-h2 my-6">
              {data.title}
            </h1>
            <p className="text-lg sm:text-xl text-[#4a4a68] font-semibold">
              {data.subtitle}
            </p>
          </div>
        </div>
      </div>
      <div className="container text-[#051F20]">
        <PortableText
          value={data.postContent}
          components={articlePortableTextComponents}
        />
        <hr className="my-16" />
      </div>
      <PageBuilder data={data.pageBuilder} />
    </div>
  );
};

export default LearnContent;
